import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Smartphone, Eye, EyeOff, Check, Sparkles, ArrowLeft } from 'lucide-react';

// Fallback plan cards used if the public pricing tiers can't be loaded.
const FALLBACK_TIERS = [
  { tier_key: 'free', name: 'Freemium', description: 'Try it out, free forever', price_monthly: 0, features: [{ text: '1 website' }, { text: '250 MB storage' }, { text: 'Community support' }] },
  { tier_key: 'basic', name: 'Starter', description: 'For small sites', price_monthly: 29, features: [{ text: '20 pages' }, { text: '5 GB storage' }, { text: 'Email support' }] },
  { tier_key: 'pro', name: 'Pro', description: 'For growing teams', price_monthly: 99, is_popular: true, features: [{ text: '100 pages' }, { text: '50 GB storage' }, { text: 'Custom domain + API' }, { text: 'Priority support' }] },
  { tier_key: 'enterprise', name: 'Enterprise', description: 'For large organizations', contact_sales: true, features: [{ text: 'Unlimited everything' }, { text: 'SSO / SAML' }, { text: 'Dedicated support' }] },
];

const Login = () => {
  const { branding, loading: brandingLoading, refreshBranding } = useBranding();
  const location = useLocation();
  // Start on the Sign Up tab when arriving via /signup or ?signup=1
  const searchParams = new URLSearchParams(location.search);
  const wantsSignup = location.pathname === '/signup' || searchParams.get('signup') === '1';
  // Plan carried in via ?plan= (e.g. "Start Free" CTA). Server re-validates this.
  const planParam = searchParams.get('plan') || null;
  const [isLogin, setIsLogin] = useState(!wantsSignup);
  // Chosen signup plan (canonical key: 'free' | 'basic' | 'pro'). Pre-set from
  // ?plan= so deep links skip the step.
  const [selectedPlan, setSelectedPlan] = useState(planParam);
  // Explicit flag for "advanced past plan picker" — decoupled from selectedPlan
  // truthiness so a tier with a blank tier_key can't get stuck on the picker.
  const [planChosen, setPlanChosen] = useState(!!planParam);
  const [pricingTiers, setPricingTiers] = useState(FALLBACK_TIERS);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    tenant_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register, verify2FA } = useAuth();
  const navigate = useNavigate();

  // Load real pricing tiers for the signup plan picker (fallback kept on error).
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/public/landing-page');
        const tiers = res?.data?.pricing_tiers;
        if (Array.isArray(tiers) && tiers.length) setPricingTiers(tiers);
      } catch (e) { /* keep fallback */ }
    })();
  }, []);

  const PLAN_LABELS = { free: 'Freemium', freemium: 'Freemium', basic: 'Starter', starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' };
  const supportEmail = branding?.support_email || 'sales@nexuscms.com';

  // Always resolve a non-empty canonical plan key from a tier, even when the
  // CMS data has a blank/missing tier_key (falls back to name, then price).
  const normalizePlanKey = (t) => {
    const k = (t.tier_key || t.tier_id || '').toString().toLowerCase().trim();
    if (['free', 'freemium'].includes(k)) return 'free';
    if (['basic', 'starter'].includes(k)) return 'basic';
    if (k === 'pro') return 'pro';
    if (k === 'enterprise') return 'enterprise';
    const n = (t.name || '').toLowerCase();
    if (n.includes('free')) return 'free';
    if (n.includes('starter') || n.includes('basic')) return 'basic';
    if (n.includes('pro')) return 'pro';
    if (n.includes('enterprise') || t.contact_sales) return 'enterprise';
    return !t.price_monthly ? 'free' : 'pro';
  };

  // A tier is "contact sales" only when explicitly flagged or it normalizes to enterprise.
  const isContactSales = (t) => !!t.contact_sales || normalizePlanKey(t) === 'enterprise';

  const handlePickPlan = (t) => {
    if (isContactSales(t)) {
      window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent('Enterprise plan enquiry')}`;
      return;
    }
    setSelectedPlan(normalizePlanKey(t));
    setPlanChosen(true);
  };

  const handleChangePlan = () => {
    setSelectedPlan(null);
    setPlanChosen(false);
  };

  const planDisplayName = PLAN_LABELS[(selectedPlan || '').toLowerCase()] || 'your plan';
  const isFreePlan = selectedPlan === 'free' || selectedPlan === 'freemium';
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        
        // Check if 2FA is required
        if (result.requires_2fa) {
          setRequires2FA(true);
          setTempToken(result.temp_token);
          toast.info('Please enter your 2FA code');
          setLoading(false);
          return;
        }
        
        toast.success('Welcome back!');
        // Re-resolve branding now that we're authenticated (applies reseller
        // white-label for reseller admins and their customers).
        refreshBranding();
        // Redirect resellers to their dashboard
        if (result.reseller_id) {
          navigate('/reseller/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        await register(formData.email, formData.password, formData.full_name, formData.tenant_name, selectedPlan);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const result = await verify2FA(tempToken, twoFactorCode);
      
      if (result.warning) {
        toast.warning(result.warning);
      }
      
      toast.success('Welcome back!');
      refreshBranding();
      if (result.user?.reseller_id) {
        navigate('/reseller/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  // Dynamic colors from branding
  const primaryColor = branding.primary_color || '#4F46E5';
  const secondaryColor = branding.secondary_color || '#7C3AED';

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}10 100%)` 
      }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {branding.logo_url ? (
            <img 
              src={branding.logo_url} 
              alt={branding.company_name} 
              className="h-12 mx-auto mb-4 object-contain"
            />
          ) : (
            <h1 
              className="text-4xl font-bold mb-2" 
              style={{ fontFamily: 'Manrope, sans-serif', color: primaryColor }}
            >
              {branding.company_name}
            </h1>
          )}
          <p className="text-slate-600">
            {branding.is_reseller ? 'Content Management Platform' : 'Multi-tenant CMS Platform'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8" data-testid="login-form">
          {requires2FA ? (
            /* 2FA Verification Form */
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="text-indigo-600" size={32} />
                </div>
                <h2 className="text-xl font-semibold">Two-Factor Authentication</h2>
                <p className="text-slate-500 text-sm mt-1">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
              
              <form onSubmit={handle2FAVerify} className="space-y-4">
                <div>
                  <Label htmlFor="twoFactorCode">Authentication Code</Label>
                  <Input
                    id="twoFactorCode"
                    type="text"
                    maxLength={8}
                    placeholder="000000"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                    className="text-center text-2xl tracking-widest"
                    autoFocus
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    You can also use a backup code
                  </p>
                </div>
                
                <Button
                  type="submit"
                  className="w-full"
                  style={{ backgroundColor: primaryColor }}
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify'}
                </Button>
                
                <button
                  type="button"
                  onClick={() => {
                    setRequires2FA(false);
                    setTempToken('');
                    setTwoFactorCode('');
                  }}
                  className="w-full text-sm text-slate-500 hover:text-slate-700"
                >
                  ← Back to login
                </button>
              </form>
            </div>
          ) : (
            /* Normal Login/Register Form */
            <>
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setIsLogin(true)}
                  data-testid="login-tab"
                  className={`flex-1 py-2 font-semibold rounded-lg transition-all`}
                  style={{
                    backgroundColor: isLogin ? primaryColor : '#f1f5f9',
                    color: isLogin ? 'white' : '#64748b'
                  }}
                >
                  Login
                </button>
                <button
                  onClick={() => setIsLogin(false)}
                  data-testid="register-tab"
                  className={`flex-1 py-2 font-semibold rounded-lg transition-all`}
                  style={{
                    backgroundColor: !isLogin ? primaryColor : '#f1f5f9',
                    color: !isLogin ? 'white' : '#64748b'
                  }}
                >
                  Sign Up
                </button>
              </div>

              {!isLogin && !planChosen ? (
                <div className="space-y-3" data-testid="plan-selector">
                  <p className="text-sm text-slate-600">
                    Choose a plan to get started. Paid plans include a <span className="font-semibold">14-day free trial</span> — no charge today, cancel anytime.
                  </p>
                  {pricingTiers.map((t, idx) => {
                    const planKey = normalizePlanKey(t);
                    const contactSales = isContactSales(t);
                    const isPaid = planKey !== 'free' && !contactSales;
                    const priceLabel = contactSales
                      ? 'Custom'
                      : (planKey === 'free' || !t.price_monthly)
                        ? 'Free'
                        : `$${t.price_monthly}`;
                    return (
                      <button
                        type="button"
                        key={t.tier_key || t.tier_id || `${planKey}-${idx}`}
                        onClick={() => handlePickPlan(t)}
                        data-testid={`plan-option-${planKey}`}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all hover:shadow-md ${t.is_popular ? 'border-indigo-400 bg-indigo-50/40' : 'border-slate-200 hover:border-indigo-300'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">{t.name || PLAN_LABELS[planKey]}</span>
                              {t.is_popular && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: primaryColor }}>
                                  <Sparkles size={11} /> Recommended
                                </span>
                              )}
                            </div>
                            {t.description && <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>}
                            <ul className="mt-2 space-y-1">
                              {(t.features || []).slice(0, 4).map((f, i) => (
                                <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                                  <Check size={13} className="text-emerald-500 flex-shrink-0" /> {f.text || f}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-lg font-bold text-slate-900">{priceLabel}</div>
                            {isPaid && <div className="text-[11px] text-slate-400">/mo after trial</div>}
                          </div>
                        </div>
                        <div className="mt-3 text-sm font-medium" style={{ color: primaryColor }}>
                          {contactSales ? 'Contact sales →' : planKey === 'free' ? 'Start free →' : `Start ${t.name || PLAN_LABELS[planKey]} trial →`}
                        </div>
                      </button>
                    );
                  })}
                  <p className="text-xs text-slate-400 text-center">You can upgrade or downgrade anytime from your dashboard.</p>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-indigo-50 border border-indigo-100" data-testid="selected-plan-banner">
                      <div className="text-sm">
                        <span className="text-slate-500">Signing up for </span>
                        <span className="font-semibold text-indigo-700">{planDisplayName}</span>
                        {!isFreePlan && <span className="text-slate-500"> · 14-day free trial</span>}
                      </div>
                      <button
                        type="button"
                        onClick={handleChangePlan}
                        className="text-xs flex items-center gap-1 text-indigo-600 hover:underline"
                        data-testid="change-plan-btn"
                      >
                        <ArrowLeft size={12} /> Change
                      </button>
                    </div>
                    <div>
                      <Label htmlFor="full_name">Full Name</Label>
                      <Input
                        id="full_name"
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required={!isLogin}
                        data-testid="full-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tenant_name">Company/Organization Name</Label>
                      <Input
                        id="tenant_name"
                        type="text"
                        value={formData.tenant_name}
                        onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                        required={!isLogin}
                        data-testid="tenant-name-input"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {isLogin && (
                  <div className="text-right">
                    <a 
                      href="/forgot-password" 
                      className="text-sm hover:underline"
                      style={{ color: primaryColor }}
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </a>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  style={{ backgroundColor: primaryColor }}
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? 'Please wait...' : isLogin ? 'Login' : 'Create Account'}
                </Button>
              </form>
              )}
            </>
          )}
          
          {/* Show reseller support info if applicable */}
          {branding.is_reseller && branding.support_email && (
            <div className="mt-6 pt-4 border-t text-center">
              <p className="text-sm text-slate-500">
                Need help? Contact <a href={`mailto:${branding.support_email}`} className="text-indigo-600 hover:underline">{branding.support_email}</a>
              </p>
            </div>
          )}
        </div>
        
        {/* Powered by badge for reseller branding */}
        {branding.is_reseller && (
          <div className="text-center mt-4">
            <p className="text-xs text-slate-400">
              Powered by <span className="font-medium">Nexus CMS</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
